/**
 * Student Onboarding Form Data Types
 * 
 * Structured 4-step form for NP students seeking preceptorships
 */

// Step 1: Education & Program Requirements
export interface StudentStep1Data {
  // Education
  school: string; // University/program name
  school_id?: number; // Optional - set when user selects from autocomplete
  program_type: 'FNP' | 'AGACNP' | 'PMHNP' | 'WHNP' | 'PNP' | 'NNP' | 'ENP';
  specialty_id: number; // Required - specialty FK id
  specialty_interest: string; // Optional/compat - specialty name
  program_year: number; // 1, 2, or 3
  expected_graduation: string; // ISO date
  gpa: number; // 0.0 - 4.0

  // Rotation Requirements
  total_hours_required: number; // e.g., 500
  hours_completed: number; // e.g., 200
  rotations_completed: number; // e.g., 2

  // Status & Eligibility
  background_check_clear: 'passed' | 'in_progress' | 'not_started';
  is_international: boolean;
  visa_status?: 'F1' | 'H1B' | 'green_card' | 'citizen' | null;

  // Nursing Credentials
  has_nursing_compact_license: boolean;
  compact_license_states?: string[]; // Array of 2-letter state codes if has_nursing_compact_license is true
}

// Step 2: Rotation Preferences & Availability
export interface StudentStep2Data {
  // Active Status
  is_seeking_rotation: boolean;

  // Availability Window
  available_start_date: string; // ISO date - when ready to start
  available_end_date: string; // ISO date - latest completion date

  // Rotation Modality
  accepts_telehealth: boolean;
  accepts_in_person: boolean;

  // Optional: Specialty Preferences
  preferred_specialties?: string[]; // e.g., ["Family Medicine", "Pediatrics"]
}

// Step 3: Contact & Bio
export interface StudentStep3Data {
  bio: string; // Professional bio/goals (max 500 chars)
  email_contacts: Array<{
    value: string; // email address
    label: string; // e.g., "Personal", "School"
  }>;
  phone_contacts: Array<{
    value: string; // phone number
    label: string; // e.g., "Mobile", "Home"
  }>;
  linkedin_profile?: string; // Optional LinkedIn URL
}

// Step 4: Location & Geographic Preferences
export interface StudentStep4Data {
  // Current Location
  address: string;
  city: string;
  state: string; // 2-letter code
  zip: string;
  country: string; // Default: "US"

  // Geographic Preferences
  willing_to_relocate: boolean;
  preferred_locations: Array<{
    name: string; // State name or region
    latitude: number;
    longitude: number;
  }>;
  max_travel_distance_miles?: number; // If not willing to relocate
}

// Combined Form Data
export interface StudentSetupFormData
  extends StudentStep1Data,
    StudentStep2Data,
    StudentStep3Data,
    StudentStep4Data {}

// Contact helper type
export interface Contact {
  value: string;
  label: string;
}

// Validation result
export interface ValidationResult {
  valid: boolean;
  errors: Record<string, string>;
}

// Form state
export interface StudentSetupFormState {
  step: 1 | 2 | 3 | 4;
  data: Partial<StudentSetupFormData>;
  errors: Record<string, string>;
  isSubmitting: boolean;
}

/**
 * NP Program Types
 */
export const NP_PROGRAM_TYPES = [
  { value: 'FNP', label: 'Family Nurse Practitioner (FNP)' },
  { value: 'AGACNP', label: 'Acute Care - Adult Gerontology (AGACNP)' },
  { value: 'PMHNP', label: 'Psychiatric-Mental Health (PMHNP)' },
  { value: 'WHNP', label: 'Womens Health (WHNP)' },
  { value: 'PNP', label: 'Pediatric (PNP)' },
  { value: 'NNP', label: 'Neonatal (NNP)' },
  { value: 'ENP', label: 'Emergency (ENP)' },
] as const;

/**
 * Nursing Compact States (2024)
 * These are the states that are part of the Nursing Compact
 */
export const NURSING_COMPACT_STATES = [
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
] as const;

/**
 * Visa Status Options
 */
export const VISA_STATUS_OPTIONS = [
  { value: 'F1', label: 'F1 Student Visa' },
  { value: 'H1B', label: 'H1B Work Visa' },
  { value: 'green_card', label: 'Green Card' },
  { value: 'citizen', label: 'U.S. Citizen' },
] as const;

/**
 * Background Check Status Options
 */
export const BACKGROUND_CHECK_STATUS_OPTIONS = [
  { value: 'passed', label: 'Passed ✓', color: 'green' },
  { value: 'in_progress', label: 'In Progress ⏳', color: 'yellow' },
  { value: 'not_started', label: 'Not Started ✕', color: 'red' },
] as const;

/**
 * Common Nursing Specialties
 */
export const NURSING_SPECIALTIES = [
  'Family Medicine',
  'Internal Medicine',
  'Pediatrics',
  'Psychiatry & Mental Health',
  'Womens Health',
  'Acute Care',
  'Emergency Medicine',
  'Cardiology',
  'Dermatology',
  'Orthopedics',
  'Oncology',
] as const;
